import { ReactElement } from 'react';
import { alpha, Button, Tooltip } from '@mui/material';
import { makeStyles } from 'tss-react/mui';

import { ONE, ZERO } from 'modules/common';
import { useGetStatsQuery } from 'modules/dashboard/actions/getStats';
import poolsIcon from 'modules/defi/icons/pools-finance.png';
import { Dialog, KnownDialogs, useDialog } from 'modules/dialogs/index';
import {
  globalTranslation,
  mergeTranslations,
  useTranslation,
} from 'modules/i18n';
import { SuccessIcon } from 'modules/icons';

import { translation } from './translation';

const mergedTranslation = mergeTranslations(globalTranslation, translation);

const isSubtitleDisabled = true;
const isExploreOpportunitiesDisabled = true;

export function SuccessStakeDialog(): ReactElement {
  const {
    isOpened,
    onClose,
    amount = ZERO,
  } = useDialog(KnownDialogs.successStake);
  const { classes } = useStyles();

  const { t, keys } = useTranslation(mergedTranslation);

  const { data } = useGetStatsQuery();
  const rate = data?.rate ?? ONE;

  const stakedAmount = rate.multipliedBy(amount);

  return (
    <Dialog open={isOpened} onClose={onClose}>
      <div className={classes.header}>
        <SuccessIcon />

        {t(keys.success)}
      </div>

      <div className={classes.description}>
        {t(keys.stakedValue, {
          value: amount.toFixed(4),
          nativeToken: t(keys.tokens.native),
        })}

        {!isSubtitleDisabled && (
          <div className={classes.subtitle}>
            {t(keys.justSentValue, {
              value: stakedAmount.toFixed(4),
              stakedToken: t(keys.tokens.staked),
            })}
          </div>
        )}
      </div>

      {!isExploreOpportunitiesDisabled && (
        <div className={classes.pools}>
          {t(keys.exploreOpportunities, { stakedToken: t(keys.tokens.staked) })}

          <Tooltip title={t(keys.pools)}>
            <img alt={t(keys.pools)} className={classes.icon} src={poolsIcon} />
          </Tooltip>
        </div>
      )}

      <Button className={classes.action} size="large" onClick={onClose}>
        {t(keys.exploreStrategies)}
      </Button>
    </Dialog>
  );
}

export const useStyles = makeStyles()(theme => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(3),
    color: theme.palette.secondary.contrastText,
    fontSize: 24,
    fontWeight: 600,
  },
  description: {
    color: theme.palette.secondary.contrastText,
    marginTop: theme.spacing(2.5),
    fontSize: 16,
    fontWeight: 500,
  },
  subtitle: {
    color: alpha(theme.palette.secondary.contrastText, 0.6),
    marginTop: theme.spacing(1.5),
    fontSize: 16,
    fontWeight: 400,
  },
  pools: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    color: theme.palette.secondary.contrastText,
    background: theme.palette.primary.contrastText,
    border: `2px solid ${theme.palette.background.default}`,
    fontSize: 16,
    fontWeight: 500,
    padding: theme.spacing(5, 4),
    marginTop: theme.spacing(6),
    borderRadius: 12,
  },
  icon: {
    width: 41,

    ':hover': {
      transform: 'translateY(-2px)',
    },
  },
  action: {
    width: '100%',
    marginTop: theme.spacing(6),
  },
}));
